import { Link } from "@heroui/react";
import { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { Badge, BadgeProps } from "@/components/Badge";
import { DownloadButton } from "@/components/DownloadButton";

import { getAuditColumns } from "@/pages/a7/Audit/getAuditColumns";

export type A7FIATimeSerieFile = {
  id: number;
  date: Date;
  error_message: string;
  status: string;
  key: string;
  instrument_id?: number;
  fund_name?: string;
  comment?: string;
};

const tableName = "a7_fia_time_serie_files";

export const getTimeSerieFilesColumns = (
  t: TFunction,
): ColumnDef<A7FIATimeSerieFile>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "Id"),
    },
    {
      accessorKey: "date",
      header: tTable("date", "Date"),
    },
    {
      accessorKey: "fund_name",
      header: tTable("fund_name", "Fund"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_instruments/${row.original.instrument_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("fund_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "status",
      header: tTable("status", "Status"),
      cell: ({ getValue }) => {
        const value = getValue();

        return <Badge color={value as BadgeProps["color"]} />;
      },
    },
    {
      accessorKey: "error_message",
      header: tTable("error_message", "Error message"),
      cell: ({ getValue }) => {
        const value = getValue() as string;
        const maxLength = 300;
        return (
          <div>
            {value
              ? value.length > maxLength
                ? `${value.substring(0, maxLength)}...`
                : value
              : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "comment",
      header: tTable("comment", "Comment"),
      cell: ({ getValue }) => {
        const value = getValue() as string;
        const maxLength = 300;
        return (
          <div>
            {value
              ? value.length > maxLength
                ? `${value.substring(0, maxLength)}...`
                : value
              : ""}
          </div>
        );
      },
    },
    {
      accessorKey: "filename",
      header: tTable("filename", "Filename"),
    },
    {
      id: "actions",
      header: () => (
        <div className="text-center">{tTable("download", "Download")}</div>
      ),
      size: 10,
      cell: ({ row }) => {
        const href = `${
          import.meta.env.VITE_BACKEND_URL
        }/a7/a7_fia_time_serie_files/${row.getValue("id")}/download`;

        return (
          <div className="flex justify-center">
            <DownloadButton
              href={href}
              filename={row.getValue("filename")}
              isIconOnly
            />
          </div>
        );
      },
    },
    {
      id: "actions_2",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getTimeSerieFilesAuditColumns = (t: TFunction) =>
  getAuditColumns<A7FIATimeSerieFile>(getTimeSerieFilesColumns(t)).filter(
    (column) => {
      const key =
        (column as { accessorKey?: string; id?: string }).accessorKey ??
        column.id;
      return key !== "fund_name" && key !== "id";
    },
  );
